/**
 * CAPTCHA Utility Functions
 * Simple math-based CAPTCHA implementation
 */

class CaptchaManager {
    constructor() {
        this.currentAnswer = 0;
        this.num1 = 0;
        this.num2 = 0;
    }

    /**
     * Generate a new CAPTCHA question
     * @param {string} formId - The form ID to target
     */
    generateCaptcha(formId) {
        // Generate random numbers between 1 and 10
        this.num1 = Math.floor(Math.random() * 10) + 1;
        this.num2 = Math.floor(Math.random() * 10) + 1;
        this.currentAnswer = this.num1 + this.num2;

        // Update the DOM elements
        const num1Element = document.querySelector(`#${formId} #num1`);
        const num2Element = document.querySelector(`#${formId} #num2`);
        const captchaInput = document.querySelector(`#${formId} #captcha`);
        const captchaError = document.querySelector(`#${formId} #captchaError`);

        if (num1Element && num2Element) {
            num1Element.textContent = this.num1;
            num2Element.textContent = this.num2;
        }

        // Clear the input and error
        if (captchaInput) {
            captchaInput.value = '';
        }
        if (captchaError) {
            captchaError.classList.add('invisible');
        }
    }

    /**
     * Validate CAPTCHA answer
     * @param {string} formId - The form ID to target
     * @returns {boolean} - True if valid, false otherwise
     */
    validateCaptcha(formId) {
        const captchaInput = document.querySelector(`#${formId} #captcha`);
        const captchaError = document.querySelector(`#${formId} #captchaError`);

        if (!captchaInput || !captchaError) {
            return false;
        }

        const userAnswer = parseInt(captchaInput.value);
        const isValid = userAnswer === this.currentAnswer;

        if (!isValid) {
            captchaError.classList.remove('invisible');
            captchaInput.focus();
        } else {
            captchaError.classList.add('invisible');
        }

        return isValid;
    }

    /**
     * Initialize CAPTCHA for a form
     * @param {string} formId - The form ID to target
     */
    initializeCaptcha(formId) {
        // Generate initial CAPTCHA
        this.generateCaptcha(formId);

        // Add refresh button event listener
        const refreshButton = document.querySelector(`#${formId} #refresh-captcha`);
        if (refreshButton) {
            refreshButton.addEventListener('click', () => {
                this.generateCaptcha(formId);
                // Trigger form validation after refresh
                this.triggerFormValidation(formId);
            });
        }

        // Add input validation on keyup
        const captchaInput = document.querySelector(`#${formId} #captcha`);
        if (captchaInput) {
            captchaInput.addEventListener('keyup', () => {
                const captchaError = document.querySelector(`#${formId} #captchaError`);
                if (captchaError && captchaError.classList.contains('invisible') === false) {
                    // Clear error when user starts typing
                    captchaError.classList.add('invisible');
                }
                // Trigger form validation on input
                this.triggerFormValidation(formId);
            });
        }
    }

    /**
     * Trigger form validation for the entire form
     * @param {string} formId - The form ID to target
     */
    triggerFormValidation(formId) {
        // Dispatch a custom event to trigger form validation
        const formElement = document.querySelector(`#${formId}`);
        if (formElement) {
            const event = new Event('input', { bubbles: true });
            formElement.dispatchEvent(event);
        }
    }
}

/**
 * Check if CAPTCHA is valid for a form
 * @param {string} formId - The form ID to check
 * @returns {boolean} - True if valid, false otherwise
 */
const isCaptchaValid = (formId) => {
    const captchaInput = document.querySelector(`#${formId} #captcha`);
    if (!captchaInput) return true; // No CAPTCHA found, consider valid

    const userAnswer = parseInt(captchaInput.value);
    const num1 = parseInt(document.querySelector(`#${formId} #num1`)?.textContent || 0);
    const num2 = parseInt(document.querySelector(`#${formId} #num2`)?.textContent || 0);
    const correctAnswer = num1 + num2;

    return userAnswer === correctAnswer;
};

/**
 * Display CAPTCHA error
 * @param {string} formId - The form ID to target
 */
const displayCaptchaError = (formId) => {
    const captchaError = document.querySelector(`#${formId} #captchaError`);
    if (captchaError) {
        captchaError.classList.remove('invisible');
    }
};

export { CaptchaManager, isCaptchaValid, displayCaptchaError };